import { Position } from 'reactflow';
import { VERTICAL_ORIENTATION } from './constants/editorConstants';

const DESIGN_NODE_TYPES = new Set(['boxNode', 'textBoxNode', 'straightLineNode', 'symbolDrawerNode']);
const DESIGN_SYMBOL_LABELS = new Set(['Box', 'TextBox', 'StraightLine', 'SymbolDrawer']);
const DEFAULT_NODE_SIZE = 100;

export const normalizeGridGap = (value) => Math.max(5, Math.min(200, Number(value) || 25));

const snapValue = (value, gridGapPx) => {
  const gap = normalizeGridGap(gridGapPx);
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric / gap) * gap;
};

export const isDesignOnlyNode = (node) => (
  DESIGN_NODE_TYPES.has(node?.type) || DESIGN_SYMBOL_LABELS.has(node?.data?.symbolLabel)
);

const pickNumeric = (...values) => {
  const found = values.find((value) => Number.isFinite(Number(value)) && Number(value) > 0);
  return found == null ? DEFAULT_NODE_SIZE : Number(found);
};

const pickPercent = (value, total) => {
  if (typeof value === 'number') return Math.max(0, Math.min(1, total ? value / total : 0));
  const text = String(value || '').trim();
  if (text.endsWith('%')) {
    const parsed = parseFloat(text.slice(0, -1));
    if (Number.isFinite(parsed)) return Math.max(0, Math.min(1, parsed / 100));
  }
  return 0.5;
};

const mapRotatedPosition = (position, angle) => {
  if (angle === VERTICAL_ORIENTATION) return position;
  switch (position) {
    case Position.Top:
    case 'top':
      return Position.Left;
    case Position.Bottom:
    case 'bottom':
      return Position.Right;
    case Position.Left:
    case 'left':
      return Position.Top;
    case Position.Right:
    case 'right':
      return Position.Bottom;
    default:
      return position;
  }
};

const getHandleOffset = (node, handle) => {
  const width = pickNumeric(node?.width, node?.style?.width);
  const height = pickNumeric(node?.height, node?.style?.height);
  const angle = typeof node?.data?.angle === 'number' ? node.data.angle : VERTICAL_ORIENTATION;
  const originalPosition = handle?.position || Position.Bottom;
  const mappedPosition = mapRotatedPosition(originalPosition, angle);
  const style = handle?.style || {};
  const fraction = (originalPosition === Position.Top || originalPosition === Position.Bottom || originalPosition === 'top' || originalPosition === 'bottom')
    ? pickPercent(style.left, width)
    : pickPercent(style.top, height);

  switch (mappedPosition) {
    case Position.Top:
    case 'top':
      return { x: width * fraction, y: 0 };
    case Position.Bottom:
    case 'bottom':
      return { x: width * fraction, y: height };
    case Position.Left:
    case 'left':
      return { x: 0, y: height * fraction };
    case Position.Right:
    case 'right':
      return { x: width, y: height * fraction };
    default:
      return { x: width / 2, y: height };
  }
};

const choosePrimaryHandle = (node) => {
  const handles = Array.isArray(node?.data?.handles) ? node.data.handles : [];
  if (handles.length > 0) {
    return handles.find((handle) => String(handle?.type || '').toLowerCase() === 'source')
      || handles.find((handle) => String(handle?.type || '').toLowerCase() === 'target')
      || handles[0];
  }
  const angle = typeof node?.data?.angle === 'number' ? node.data.angle : VERTICAL_ORIENTATION;
  return {
    type: 'source',
    position: angle === VERTICAL_ORIENTATION ? Position.Bottom : Position.Right,
  };
};

export const alignNodeToNearestGrid = (node, gridGapPx) => {
  if (!node?.position || isDesignOnlyNode(node)) return node;
  const isBusNode = node?.type === 'busNode' || node?.data?.symbolLabel === 'Bus';
  if (isBusNode) {
    const nextPosition = {
      x: snapValue(node.position.x, gridGapPx),
      y: snapValue(node.position.y, gridGapPx),
    };
    if (nextPosition.x === node.position.x && nextPosition.y === node.position.y) return node;
    return { ...node, position: { ...node.position, ...nextPosition } };
  }

  const offset = getHandleOffset(node, choosePrimaryHandle(node));
  const nextPosition = {
    x: snapValue((Number(node.position.x) || 0) + offset.x, gridGapPx) - offset.x,
    y: snapValue((Number(node.position.y) || 0) + offset.y, gridGapPx) - offset.y,
  };
  if (nextPosition.x === node.position.x && nextPosition.y === node.position.y) return node;
  return { ...node, position: { ...node.position, ...nextPosition } };
};

export const alignNodesToNearestGrid = (nodes, gridGapPx) => {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node) => alignNodeToNearestGrid(node, gridGapPx));
};
